import {
  createReactiveSystem,
  type Link,
  ReactiveFlags,
  type ReactiveNode,
} from 'alien-signals/system';

// The reactive graph below lives in module scope (queuedEffects, activeSub, batchDepth,
// cycle). A second evaluation of this module builds a second, unconnected graph: signals
// created against one copy never notify effects registered against the other, and the
// failure is silent. Under module federation this means the shared scope is not treating
// @crown/signal as a singleton.
const DUPLICATE_LOAD_MARKER = Symbol.for('crown.signal.loaded');
// SAFETY: the marker is an optional property only this module writes, so
// reading it off the global object is sound whether or not it is set.
const signalGlobal = globalThis as typeof globalThis & {
  [DUPLICATE_LOAD_MARKER]?: true;
};
// A server that re-evaluates its rebuilt bundle - a development server after an
// edit - loads a fresh copy to replace the old, not beside it; only a page can hold
// two live copies, which is the module federation mistake this names.
if (signalGlobal[DUPLICATE_LOAD_MARKER] && 'document' in globalThis) {
  console.error(
    '[@crown/signal] Loaded twice. Two independent reactive graphs now exist and effects ' +
      'will not fire across the boundary. Under module federation, @crown/signal must be a ' +
      'shared singleton in every host and remote.',
  );
} else {
  signalGlobal[DUPLICATE_LOAD_MARKER] = true;
}

export namespace Signal {
  // Generic, so a guard keeps what the caller already knew about the value.
  export let isState: <V>(s: V) => s is V & State<unknown>,
    isComputed: <V>(s: V) => s is V & Computed<unknown>,
    isWatcher: <V>(s: V) => s is V & subtle.Watcher;

  /** A node that can be `activeSub`: a computed or an effect, evaluating. */
  interface _Sub extends ReactiveNode {
    // Non-zero when what the node reads counts as watched.
    readonly watchCount: number;
  }

  // Stands in for `activeSub` while a watcher callback runs. It is only ever
  // compared by identity, never linked into the graph.
  const WATCHER_PLACEHOLDER: _Sub = {
    flags: ReactiveFlags.None,
    watchCount: 0,
  };

  enum EffectFlags {
    Queued = 1 << 6,
  }

  const { link, unlink, propagate, checkDirty, shallowPropagate } =
    createReactiveSystem({
      update(node: _Computed) {
        return node.update();
      },
      notify(node: _Watcher | _Effect) {
        const flags = node.flags;
        if (node.isEffect) {
          if (!(flags & EffectFlags.Queued)) {
            node.flags = flags | EffectFlags.Queued;
            // Notification follows subscription order, not creation order;
            // note when they differ so the flush can put them back.
            if (node.order < lastQueuedOrder) queueOutOfOrder = true;
            lastQueuedOrder = node.order;
            pendingEffects.push(node);
            scheduleEffects();
          }
          return;
        }
        if (!(flags & EffectFlags.Queued)) {
          node.flags = flags | EffectFlags.Queued;
          queuedEffects[queuedEffectsLength++] = node;
        }
      },
      unwatched(node) {
        let toRemove = node.deps;
        if (toRemove !== undefined) {
          do {
            toRemove = unlink(toRemove, node);
          } while (toRemove !== undefined);
          node.flags |= ReactiveFlags.Dirty;
        }
      },
    });
  const queuedEffects: (_Watcher | undefined)[] = [];

  // Effects wait for a microtask even when a batch ends, so a burst of writes,
  // batched or not, costs each effect one run.
  let pendingEffects: _Effect[] = [];
  let effectsScheduled = false;
  // Effects run in the order they were made. A renderer makes a parent's
  // bindings before its children's, so a parent always runs first.
  let effectsMade = 0;
  let lastQueuedOrder = 0;
  let queueOutOfOrder = false;

  function scheduleEffects(): void {
    if (!effectsScheduled) {
      effectsScheduled = true;
      queueMicrotask(runEffects);
    }
  }

  let runningEffects = false;

  function runEffects(): void {
    effectsScheduled = false;
    // A flush asked for from inside an effect is the one already running.
    if (runningEffects) return;
    runningEffects = true;
    if (queueOutOfOrder) pendingEffects = inOrder(pendingEffects);
    queueOutOfOrder = false;
    lastQueuedOrder = 0;
    // An effect that writes queues more; the loop reads the length each time.
    for (let i = 0; i < pendingEffects.length; i++) {
      const effect = pendingEffects[i];
      if (!effect) continue;
      effect.flags &= ~EffectFlags.Queued;
      try {
        effect.run();
      } catch (error) {
        // One failing effect must not strand the rest of the queue.
        console.error('[@crown/signal] an effect failed', error);
      }
    }
    pendingEffects = [];
    runningEffects = false;
    lastQueuedOrder = 0;
    queueOutOfOrder = false;
  }

  function byOrder(a: _Effect, b: _Effect): number {
    return a.order - b.order;
  }

  /** Byte counts for the radix sort, kept between flushes. */
  const buckets = new Int32Array(257);

  /**
   * The queue in the order its effects were made. A short queue is sorted as
   * it always was; a long one - thousands of effects notified in subscription
   * order - by radix on the order number, a byte at a time, with a pass
   * skipped when every effect shares that byte. That is a few passes over the
   * queue instead of a comparator call for every pair, which was a quarter of
   * a large update.
   */
  function inOrder(queue: _Effect[]): _Effect[] {
    const n = queue.length;
    if (n < 64) return queue.sort(byOrder);
    let from = queue;
    let to: _Effect[] = queue.slice();
    for (let shift = 0; shift < 32; shift += 8) {
      buckets.fill(0);
      for (let i = 0; i < n; i++) {
        const effect = from[i];
        if (!effect) continue;
        const bucket = ((effect.order >>> shift) & 255) + 1;
        buckets[bucket] = (buckets[bucket] ?? 0) + 1;
      }
      // Every effect in one bucket: this byte orders nothing.
      let single = false;
      for (let b = 1; b <= 256; b++) {
        if (buckets[b] === n) single = true;
        if (buckets[b] !== 0) break;
      }
      if (single) continue;
      for (let b = 1; b <= 256; b++)
        buckets[b] = (buckets[b] ?? 0) + (buckets[b - 1] ?? 0);
      for (let i = 0; i < n; i++) {
        const effect = from[i];
        if (!effect) continue;
        const bucket = (effect.order >>> shift) & 255;
        const at = buckets[bucket] ?? 0;
        to[at] = effect;
        buckets[bucket] = at + 1;
      }
      const swap = from;
      from = to;
      to = swap;
    }
    return from;
  }

  let cycle = 0;
  let notifyIndex = 0;
  let queuedEffectsLength = 0;
  let activeSub: _Sub | undefined;

  /** Makes `node` the one tracking reads, and returns the one it replaced. */
  function enterSub(node: _Sub): _Sub | undefined {
    const previous = activeSub;
    activeSub = node;
    return previous;
  }
  let batchDepth = 0;
  let flushScheduled = false;

  function flush(): void {
    while (notifyIndex < queuedEffectsLength) {
      const effect = queuedEffects[notifyIndex];
      queuedEffects[notifyIndex++] = undefined;
      if (effect === undefined) {
        continue;
      }
      effect.flags &= ~EffectFlags.Queued;
      effect.run();
    }
    notifyIndex = 0;
    queuedEffectsLength = 0;
  }

  function scheduleFlush(): void {
    if (!flushScheduled) {
      flushScheduled = true;
      queueMicrotask(() => {
        flushScheduled = false;
        flush();
      });
    }
  }

  class _State<T = unknown> implements ReactiveNode, State<T> {
    subs: Link | undefined = undefined;
    subsTail: Link | undefined = undefined;
    flags: ReactiveFlags = ReactiveFlags.None;
    watchCount = 0;
    previousValue: T;

    constructor(
      private value: T,
      private options?: Options<T>,
    ) {
      this.previousValue = value;
      if (options?.equals !== undefined) {
        this.equals = options.equals;
      }
    }

    equals(t: T, t2: T): boolean {
      return Object.is(t, t2);
    }

    onWatched() {
      if (this.watchCount++ === 0) {
        this.options?.[subtle.watched]?.call(this);
      }
    }

    onUnwatched() {
      if (--this.watchCount === 0) {
        this.options?.[subtle.unwatched]?.call(this);
      }
    }

    get(): T {
      if (activeSub === WATCHER_PLACEHOLDER) {
        throw new Error('Cannot read from state inside watcher');
      }
      if (activeSub !== undefined) {
        const lastLink = this.subsTail;
        link(this, activeSub, cycle);
        const newLink = this.subsTail;
        // A new link is the one just made for `activeSub`. A watched
        // computed or an effect keeps what it reads watched.
        if (
          newLink !== undefined &&
          newLink !== lastLink &&
          activeSub.watchCount
        ) {
          this.onWatched();
        }
      }
      return this.value;
    }

    set(value: T): void {
      if (activeSub === WATCHER_PLACEHOLDER) {
        throw new Error('Cannot write to state inside watcher');
      }
      if (!this.equals(this.value, value)) {
        this.value = value;
        const subs = this.subs;
        if (subs !== undefined) {
          // alien-signals 3.x added `innerWrite`: true marks dependents Dirty
          // rather than merely Pending, which is what a write performed *during*
          // an evaluation requires - otherwise a computed that wrote to a state
          // it also reads can be left reading the old value. Their own signal
          // passes `!!runDepth`; the equivalent here is whether a subscriber is
          // currently evaluating. The watcher case is already refused above.
          propagate(subs, activeSub !== undefined);
          shallowPropagate(subs);
          if (batchDepth === 0) {
            scheduleFlush();
          }
        }
      }
    }
  }

  export interface State<T> {
    get(): T;
    set(value: T): void;
  }

  export const State: {
    new <T>(value: T, options?: Options<T>): State<T>;
  } = _State;
  // A brand check by class: a private `#brand` did the same, but built to a
  // helper call on every signal created.
  isState = <V>(s: V): s is V & State<unknown> => s instanceof _State;

  class _Computed<T = unknown> implements ReactiveNode, Computed<T> {
    subs: Link | undefined = undefined;
    subsTail: Link | undefined = undefined;
    deps: Link | undefined = undefined;
    depsTail: Link | undefined = undefined;
    flags = ReactiveFlags.Mutable | ReactiveFlags.Dirty;
    isError = true;
    watchCount = 0;
    value: T | undefined = undefined;

    constructor(
      private getter: () => T,
      private options?: Options<T>,
    ) {
      if (options?.equals !== undefined) {
        this.equals = options.equals;
      }
    }

    equals(t: T, t2: T): boolean {
      return Object.is(t, t2);
    }

    onWatched() {
      if (this.watchCount++ === 0) {
        this.options?.[subtle.watched]?.call(this);
        for (let edge = this.deps; edge !== undefined; edge = edge.nextDep) {
          // SAFETY: only a State or a Computed is ever linked as a dependency;
          // each links itself from get(), and watch() admits nothing else.
          const dep = edge.dep as _AnySignal;
          dep.onWatched();
        }
      }
    }

    onUnwatched() {
      if (--this.watchCount === 0) {
        this.options?.[subtle.unwatched]?.call(this);
        for (let edge = this.deps; edge !== undefined; edge = edge.nextDep) {
          // SAFETY: only a State or a Computed is ever linked as a dependency.
          const dep = edge.dep as _AnySignal;
          dep.onUnwatched();
        }
      }
    }

    get(): T {
      if (activeSub === WATCHER_PLACEHOLDER) {
        throw new Error('Cannot read from computed inside watcher');
      }
      const flags = this.flags;
      if (flags & ReactiveFlags.RecursedCheck) {
        throw new Error('Cycles detected');
      }
      if (
        flags & ReactiveFlags.Dirty ||
        (flags & ReactiveFlags.Pending &&
          this.deps !== undefined &&
          checkDirty(this.deps, this))
      ) {
        if (this.update()) {
          const subs = this.subs;
          if (subs !== undefined) {
            shallowPropagate(subs);
          }
        }
      } else if (flags & ReactiveFlags.Pending) {
        this.flags = flags & ~ReactiveFlags.Pending;
      }
      if (activeSub !== undefined) {
        const lastLink = this.subsTail;
        link(this, activeSub, cycle);
        const newLink = this.subsTail;
        // A new link is the one just made for `activeSub`. A watched
        // computed or an effect keeps what it reads watched.
        if (
          newLink !== undefined &&
          newLink !== lastLink &&
          activeSub.watchCount
        ) {
          this.onWatched();
        }
      }
      if (this.isError) {
        throw this.value;
      }
      // SAFETY: the getter has run and did not throw, so `value` holds its result.
      return this.value as T;
    }

    update(): boolean {
      const prevSub = enterSub(this);
      ++cycle;
      this.depsTail = undefined;
      // Mutable | RecursedCheck, written as the number so no lookup is made.
      this.flags = 5;
      const oldValue = this.value;
      try {
        const newValue = this.getter();
        // SAFETY: when `isError` is false, `value` holds the getter's last result.
        if (this.isError || !this.equals(oldValue as T, newValue)) {
          this.isError = false;
          this.value = newValue;
          return true;
        }
        return false;
      } catch (err) {
        // SAFETY: while `isError` is set, `value` holds what the getter threw,
        // not a T: equals is only asked to compare two thrown values, and get()
        // throws `value` without reading it as a T.
        if (!this.isError || !this.equals(oldValue as T, err as T)) {
          this.isError = true;
          // SAFETY: a thrown value, stored while `isError` is set, as above.
          this.value = err as T;
          return true;
        }
        return false;
      } finally {
        const watched = !!this.watchCount;
        // SAFETY: the getter may have linked dependencies since, but TypeScript
        // still holds `depsTail` at the `undefined` written before it ran.
        let toRemove =
          this.depsTail !== undefined
            ? (this.depsTail as Link).nextDep
            : this.deps;
        while (toRemove !== undefined) {
          if (watched) {
            // SAFETY: only a State or a Computed is ever linked as a dependency.
            const dep = toRemove.dep as _AnySignal;
            dep.onUnwatched();
          }
          toRemove = unlink(toRemove, this);
        }
        activeSub = prevSub;
        this.flags &= ~(4 satisfies ReactiveFlags.RecursedCheck);
      }
    }
  }

  export interface Computed<T> {
    get(): T;
  }

  export const Computed: {
    new <T>(getter: () => T, options?: Options<T>): Computed<T>;
  } = _Computed;
  isComputed = <V>(c: V): c is V & Computed<unknown> => c instanceof _Computed;

  // A function rather than a `#private` method: browsers the public form page
  // supports lack private methods, so each one costs a set of helper functions
  // in every bundle that carries a watcher.
  function assertSignals(signals: _AnySignal[]): void {
    for (const signal of signals) {
      if (!isComputed(signal) && !isState(signal)) {
        throw new TypeError(
          'Called watch/unwatch without a Computed or State argument',
        );
      }
    }
  }

  class _Watcher implements ReactiveNode, subtle.Watcher {
    // Told apart from an effect by a prototype field, not a prototype walk: notify asks for every node it queues.
    declare readonly isEffect: false;
    deps: Link | undefined = undefined;
    depsTail: Link | undefined = undefined;
    flags = ReactiveFlags.Watching;
    watchList = new Map<_AnySignal, Link>();

    constructor(private fn: () => void) {}

    run() {
      const prevSub = activeSub;
      activeSub = WATCHER_PLACEHOLDER;
      this.flags &= ~(ReactiveFlags.Dirty | ReactiveFlags.Pending);
      try {
        this.fn();
      } finally {
        activeSub = prevSub;
      }
    }

    watch(...signals: _AnySignal[]): void {
      if (!isWatcher(this)) {
        throw new TypeError('Called watch without Watcher receiver');
      }
      assertSignals(signals);

      for (const signal of signals) {
        if (this.watchList.has(signal)) {
          continue;
        }
        signal.onWatched();
        link(signal, this, 0);
        const depsTail = this.depsTail;
        if (depsTail !== undefined) {
          this.watchList.set(signal, depsTail);
        }
      }
    }

    unwatch(...signals: _AnySignal[]): void {
      if (!isWatcher(this)) {
        throw new TypeError('Called unwatch without Watcher receiver');
      }
      assertSignals(signals);

      for (const signal of signals) {
        const watchLink = this.watchList.get(signal);
        if (watchLink === undefined) {
          continue;
        }
        signal.onUnwatched();
        unlink(watchLink, this);
        this.watchList.delete(signal);
      }
    }

    getPending(): _AnySignal[] {
      if (!isWatcher(this)) {
        throw new TypeError('Called getPending without Watcher receiver');
      }
      const arr: _AnySignal[] = [];
      for (let edge = this.deps; edge !== undefined; edge = edge.nextDep) {
        const source = edge.dep;
        if (
          source.flags & (ReactiveFlags.Dirty | ReactiveFlags.Pending) &&
          source instanceof _Computed
        ) {
          arr.push(source);
        }
      }
      return arr;
    }
  }

  /**
   * An effect as a node of the graph: it links to what it reads and is queued
   * only when one of those changes, so a write costs the effects that read it
   * and nothing else. It re-runs on the flush after the write, or at the end
   * of a batch.
   */
  class _Effect implements ReactiveNode {
    declare readonly isEffect: true;
    deps: Link | undefined = undefined;
    depsTail: Link | undefined = undefined;
    subs: Link | undefined = undefined;
    subsTail: Link | undefined = undefined;
    flags: ReactiveFlags = ReactiveFlags.Watching;
    // Non-zero, so what the effect reads counts as watched.
    readonly watchCount = 1;
    cleanup: (() => void) | undefined = undefined;
    readonly order = ++effectsMade;

    constructor(private readonly fn: () => void | (() => void)) {}

    run(): void {
      const flags = this.flags;
      if (
        flags & ReactiveFlags.Dirty ||
        (flags & ReactiveFlags.Pending &&
          this.deps !== undefined &&
          checkDirty(this.deps, this))
      ) {
        this.execute();
      } else {
        this.flags = flags & ~ReactiveFlags.Pending;
      }
    }

    execute(): void {
      // Most effects have no cleanup: asked here, it costs no call.
      if (this.cleanup !== undefined) this.runCleanup();
      if (!(this.flags & ReactiveFlags.Watching)) return;
      const prevSub = enterSub(this);
      ++cycle;
      this.depsTail = undefined;
      this.flags = ReactiveFlags.Watching | ReactiveFlags.RecursedCheck;
      try {
        const cleanup = this.fn();
        if (isCleanup(cleanup)) this.cleanup = cleanup;
      } finally {
        activeSub = prevSub;
        this.flags &= ~ReactiveFlags.RecursedCheck;
        // SAFETY: the effect may have linked dependencies since, but TypeScript
        // still holds `depsTail` at the `undefined` written before it ran.
        let toRemove =
          this.depsTail !== undefined
            ? (this.depsTail as Link).nextDep
            : this.deps;
        while (toRemove !== undefined) {
          // SAFETY: only a State or a Computed is ever linked as a dependency.
          (toRemove.dep as _AnySignal).onUnwatched();
          toRemove = unlink(toRemove, this);
        }
      }
    }

    runCleanup(): void {
      const cleanup = this.cleanup;
      if (cleanup === undefined) return;
      this.cleanup = undefined;
      const prevSub = activeSub;
      activeSub = undefined;
      try {
        cleanup();
      } finally {
        activeSub = prevSub;
      }
    }

    dispose(): void {
      this.flags = ReactiveFlags.None;
      let toRemove = this.deps;
      while (toRemove !== undefined) {
        // SAFETY: only a State or a Computed is ever linked as a dependency.
        (toRemove.dep as _AnySignal).onUnwatched();
        toRemove = unlink(toRemove, this);
      }
      this.runCleanup();
    }
  }

  // One value on each prototype rather than a field on every instance.
  Object.defineProperty(_Watcher.prototype, 'isEffect', { value: false });
  Object.defineProperty(_Effect.prototype, 'isEffect', { value: true });

  isWatcher = <V>(w: V): w is V & subtle.Watcher => w instanceof _Watcher;

  /**
   * What an effect returned is kept as its cleanup only when it is a function:
   * a callback typed to return void may still return a value.
   */
  function isCleanup(result: void | (() => void)): result is () => void {
    return typeof result === 'function';
  }

  type _AnySignal<T = unknown> = _State<T> | _Computed<T>;
  type _AnySink = _Computed<unknown> | _Watcher;

  // A signal of any value type. `State<T>` both reads and writes T, so a union
  // over a type parameter would reject a `State<number>` here; the read-only
  // shape, which is `Computed<unknown>`, admits every State and Computed.
  type AnySignal = Computed<unknown>;
  type AnySink = Computed<unknown> | subtle.Watcher;

  export namespace subtle {
    export function untrack<T>(fn: () => T) {
      const prevSub = activeSub;
      activeSub = undefined;
      try {
        return fn();
      } finally {
        activeSub = prevSub;
      }
    }

    export interface Watcher {
      watch(...signals: AnySignal[]): void;
      unwatch(...signals: AnySignal[]): void;
      getPending(): AnySignal[];
    }

    export const Watcher: {
      new (fn: () => void): Watcher;
    } = _Watcher;

    export function hasSinks(signal: AnySignal) {
      if (!(signal instanceof _Computed) && !(signal instanceof _State)) {
        throw new TypeError('Called hasSinks without a Signal argument');
      }
      return signal.watchCount > 0;
    }

    export function hasSources(signal: AnySink) {
      if (!(signal instanceof _Computed) && !(signal instanceof _Watcher)) {
        throw new TypeError(
          'Called hasSources without a Computed or Watcher argument',
        );
      }
      return signal.depsTail !== undefined;
    }

    export function introspectSinks(signal: AnySignal): AnySink[] {
      if (!(signal instanceof _Computed) && !(signal instanceof _State)) {
        throw new TypeError('Called introspectSinks without a Signal argument');
      }
      const arr: _AnySink[] = [];
      for (let edge = signal.subs; edge !== undefined; edge = edge.nextSub) {
        // SAFETY: a subscriber is a Computed, a Watcher or an effect. An effect
        // has no public type (the proposal this API follows has none), so it
        // is listed as a sink its caller can only compare by identity.
        arr.push(edge.sub as _AnySink);
      }
      return arr;
    }

    export function introspectSources(sink: AnySink): AnySignal[] {
      if (!(sink instanceof _Computed) && !(sink instanceof _Watcher)) {
        throw new TypeError(
          'Called introspectSources without a Computed or Watcher argument',
        );
      }
      const arr: _AnySignal[] = [];
      for (let edge = sink.deps; edge !== undefined; edge = edge.nextDep) {
        // SAFETY: only a State or a Computed is ever linked as a dependency.
        arr.push(edge.dep as _AnySignal);
      }
      return arr;
    }

    export function currentComputed(): Computed<unknown> | undefined {
      if (isComputed(activeSub)) {
        return activeSub;
      }
    }

    export function batch<T>(fn: () => T): T {
      batchDepth++;
      try {
        return fn();
      } finally {
        if (--batchDepth === 0) {
          flush();
        }
      }
    }

    /**
     * Runs `fn` now and again after any signal it read changes, until the
     * returned function is called. `fn` may return a cleanup, which runs before
     * each re-run and on disposal. Not part of the TC39 proposal.
     */
    export function effect(fn: () => void | (() => void)): () => void {
      const node = new _Effect(fn);
      node.execute();
      return () => node.dispose();
    }

    /**
     * The same effect, returned as the object that disposes it: a caller that
     * keeps many (a renderer, one per binding) saves a closure each. Null when
     * the first run read no signal and left no cleanup: nothing can run it
     * again, so there is nothing to keep — a binding to a constant costs one
     * run and no memory after it.
     */
    export function effectNode(
      fn: () => void | (() => void),
    ): { dispose(): void } | null {
      const node = new _Effect(fn);
      node.execute();
      return node.deps === undefined && node.cleanup === undefined
        ? null
        : node;
    }

    /** Runs every queued effect now instead of on the next microtask. */
    export function flushEffects(): void {
      runEffects();
    }

    export const watched = Symbol('watched');
    export const unwatched = Symbol('unwatched');
  }

  export interface Options<T> {
    equals?: (this: AnySignal, t: T, t2: T) => boolean;

    [subtle.watched]?: (this: AnySignal) => void;

    [subtle.unwatched]?: (this: AnySignal) => void;
  }
}
